import type { ComponentType } from 'react'
import type {
  ActivityClientModule,
  ActivityPersistentLinkBuilderProps,
  ActivityPersistentSoloLaunchParams,
  ActivityPersistentSoloLaunchResult,
} from '../../../types/activity.js'
import ResonanceManager from './manager/ResonanceManager.js'
import ResonanceStudent from './student/ResonanceStudent.js'
import { validateQuestionSet } from '../shared/validation.js'
import ResonancePersistentLinkBuilder from './tools/ResonancePersistentLinkBuilder.js'
import ResonanceToolShell from './tools/ResonanceToolShell.js'

const SOLO_LAUNCH_LOGGED_ERROR_LIMIT = 10
const SOLO_LAUNCH_MESSAGE_ERROR_LIMIT = 10
const SOLO_LAUNCH_MAX_MESSAGE_LENGTH = 1000

function formatSoloQuestionSetErrorMessage(errors: readonly string[]): string {
  const baseMessage = 'Resonance solo entry requires a valid question set.'
  if (errors.length === 0) {
    return baseMessage
  }

  const includedErrors = errors.slice(0, SOLO_LAUNCH_MESSAGE_ERROR_LIMIT)
  const remainingErrorCount = errors.length - includedErrors.length
  const details = includedErrors.join('; ')
  const suffix = remainingErrorCount > 0 ? `; ...and ${remainingErrorCount} more error(s)` : ''
  const fullMessage = `${baseMessage.slice(0, -1)}: ${details}${suffix}`

  if (fullMessage.length <= SOLO_LAUNCH_MAX_MESSAGE_LENGTH) {
    return fullMessage
  }

  return `${fullMessage.slice(0, SOLO_LAUNCH_MAX_MESSAGE_LENGTH - 3)}...`
}

export async function launchResonancePersistentSoloEntry(
  params: ActivityPersistentSoloLaunchParams,
): Promise<ActivityPersistentSoloLaunchResult> {
  const rawEncodedQuestions = params.selectedOptions?.q
  const rawPersistentHash = params.selectedOptions?.h
  const encodedQuestions = typeof rawEncodedQuestions === 'string' ? rawEncodedQuestions.trim() : null
  const persistentHash = typeof rawPersistentHash === 'string' ? rawPersistentHash.trim() : null
  const rawQuestions = params.selectedOptions?.questions
  const presentationMode = params.selectedOptions?.presentationMode === 'staged' ? 'staged' : 'standard'
  const validatedQuestionSet = rawQuestions !== undefined
    ? validateQuestionSet(rawQuestions)
    : null
  const validatedQuestions = validatedQuestionSet?.errors.length === 0
    ? validatedQuestionSet.questions
    : []

  if ((!encodedQuestions || !persistentHash) && validatedQuestions.length === 0) {
    const validationErrors = validatedQuestionSet?.errors ?? []
    if (validationErrors.length > 0) {
      console.error('[Resonance][SoloLaunchInvalidQuestions]', {
        errorCount: validationErrors.length,
        errors: validationErrors.slice(0, SOLO_LAUNCH_LOGGED_ERROR_LIMIT),
        questionCount: Array.isArray(rawQuestions) ? rawQuestions.length : 0,
      })
    }

    throw new Error(formatSoloQuestionSetErrorMessage(validationErrors))
  }

  const createResponse = await fetch('/api/resonance/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...(encodedQuestions && persistentHash ? { encodedQuestions, persistentHash } : {}),
      ...(validatedQuestions.length > 0 ? { questions: validatedQuestions } : {}),
      presentationMode,
      selfPacedMode: true,
    }),
  })

  if (!createResponse.ok) {
    throw new Error('Failed to create Resonance solo session.')
  }

  const created = (await createResponse.json()) as {
    id?: unknown
  }

  if (typeof created.id !== 'string' || created.id.length === 0) {
    throw new Error('Resonance solo session response was invalid.')
  }

  return {
    sessionId: created.id,
  }
}

const resonanceActivity: ActivityClientModule = {
  ManagerComponent: ResonanceManager as ComponentType<unknown>,
  StudentComponent: ResonanceStudent as ComponentType<unknown>,
  UtilComponent: ResonanceToolShell as ComponentType<unknown>,
  PersistentLinkBuilderComponent: ResonancePersistentLinkBuilder as ComponentType<ActivityPersistentLinkBuilderProps>,
  footerContent: null,
  launchPersistentSoloEntry: launchResonancePersistentSoloEntry,
}

export default resonanceActivity
